import { useMemo, useState } from "react";

import {
  DESIGN_DIMENSIONS,
  INTERPOLANT_DOMAIN,
  PRESET_QUERIES,
  evaluateInterpolantChain,
  type ArtifactNode,
  type DesignVector,
  type DimensionKey,
} from "./interpolantProvenanceModel";
import { InterpolantDerivedPlot } from "./InterpolantDerivedPlot";
import {
  artifactBeatState,
  InterpolantSlideAnimation,
  INTERPOLANT_ANIMATION_STEPS,
  type InterpolantAnimationStep,
} from "./InterpolantSlideAnimation";
import { InterpolantSupport2D } from "./InterpolantSupport2D";
import { InterpolantSupport3D } from "./InterpolantSupport3D";
import "./InterpolantProvenanceDemo.css";

const DEFAULT_ANIMATION_STEP = INTERPOLANT_ANIMATION_STEPS[0]!;
const DEFAULT_QUERY = PRESET_QUERIES.find((query) => query.id === DEFAULT_ANIMATION_STEP.presetId) ?? PRESET_QUERIES[0]!;
const ARTIFACT_RECORD_IDS: Record<string, string> = {
  "aircraft-surrogate": "aDB-CERT-042.CL",
  ld: "aDB-CERT-042.LD",
  "lift-reserve": "aDB-CERT-042.CL_RES",
  "pitch-margin": "aDB-CERT-042.CM_MARGIN",
  "cert-margin": "aDB-CERT-042.REQ_MARGIN",
};

const ARTIFACT_OPERATIONS: Record<string, string> = {
  "aircraft-surrogate": "op-adb-cert-042-cl-from-surrogate",
  ld: "op-adb-cert-042-ld",
  "lift-reserve": "op-adb-cert-042-cl-reserve",
  "pitch-margin": "op-adb-cert-042-cm-margin",
  "cert-margin": "op-adb-cert-042-requirement-margin",
};

function statusClass(mode: ArtifactNode["mode"]) {
  return `ipd-status ${mode}`;
}

function percent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function trimDecimal(value: string) {
  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
}

function formatValue(value: number, unit: string) {
  const digits = Math.abs(value) >= 100 ? 0 : unit === "" ? 2 : 1;
  return `${trimDecimal(value.toFixed(digits))}${unit ? ` ${unit}` : ""}`;
}

function artifactRecordId(artifact: ArtifactNode) {
  return artifact.dataPointId;
}

function artifactOperationId(artifact: ArtifactNode) {
  return ARTIFACT_OPERATIONS[artifact.id] ?? `op-derived-${artifact.id}`;
}

function ArtifactAuditList({
  artifacts,
  selectedArtifactId,
  step,
  onSelectArtifact,
}: {
  artifacts: ArtifactNode[];
  selectedArtifactId: string;
  step: InterpolantAnimationStep;
  onSelectArtifact: (artifactId: string) => void;
}) {
  return (
    <div className="ipd-artifact-list" data-testid="artifact-audit-list">
      {artifacts.map((artifact) => {
        const beatState = artifactBeatState(artifact.id, step);
        return (
          <button
            type="button"
            key={artifact.id}
            className="ipd-artifact-row"
            aria-pressed={selectedArtifactId === artifact.id}
            data-beat-state={beatState}
            data-testid={`artifact-audit-row-${artifact.id}`}
            onClick={() => onSelectArtifact(artifact.id)}
          >
            <span>
              <strong>{artifact.dataPointId}</strong>
              <small>
                {beatState === "queued" ? "not in this beat" : `${artifact.quantityLabel} · ${artifact.conditionLabel}`}
              </small>
            </span>
            <em className={beatState === "queued" ? "queued" : artifact.mode}>
              {beatState === "queued" ? "queued" : artifact.mode}
            </em>
          </button>
        );
      })}
    </div>
  );
}

function ArtifactChain({
  artifacts,
  selectedArtifactId,
  onSelectArtifact,
}: {
  artifacts: ArtifactNode[];
  selectedArtifactId: string;
  onSelectArtifact: (artifactId: string) => void;
}) {
  return (
    <section className="ipd-chain" aria-label="Selected aDB data point lineage">
      {artifacts.map((artifact) => (
        <button
          type="button"
          key={artifact.id}
          className="ipd-node"
          aria-pressed={selectedArtifactId === artifact.id}
          onClick={() => onSelectArtifact(artifact.id)}
          data-testid={`artifact-node-${artifact.id}`}
        >
          <span className="ipd-node-kind">aDB point</span>
          <span className="ipd-node-label">{artifact.dataPointId}</span>
          <span className="ipd-node-value">{artifact.value}</span>
          <span className={statusClass(artifact.mode)}>{artifact.mode}</span>
          <span className="ipd-node-formula">{artifact.quantityLabel} · {artifact.formula}</span>
        </button>
      ))}
    </section>
  );
}

function ArtifactTable({ artifacts }: { artifacts: ArtifactNode[] }) {
  return (
    <div className="ipd-table-scroll" data-testid="artifact-provenance-table">
      <table className="ipd-artifact-table">
        <thead>
          <tr>
            <th>aDB data point</th>
            <th>Operation</th>
            <th>Value</th>
            <th>Status</th>
            <th>Inputs / parents</th>
            <th>CFD support cases</th>
          </tr>
        </thead>
        <tbody>
          {artifacts.map((artifact) => (
            <tr key={artifact.id}>
              <td>
                <strong>{artifact.dataPointId}</strong>
                <div className="ipd-muted">{artifact.quantityLabel} · {artifact.conditionLabel}</div>
              </td>
              <td className="ipd-mono">{artifactOperationId(artifact)}</td>
              <td className="ipd-mono">{artifact.value}</td>
              <td>
                <span className={statusClass(artifact.mode)}>{artifact.mode}</span>
                <div className="ipd-muted">confidence {percent(artifact.confidence)}</div>
              </td>
              <td className="ipd-mono">{artifact.parentIds.map((parentId) => ARTIFACT_RECORD_IDS[parentId] ?? parentId).join(", ")}</td>
              <td className="ipd-mono">{artifact.supportIds.join(", ")}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function InterpolantProvenanceDemo({
  embedded = false,
  initialMapView = "3d",
  compact = false,
}: {
  embedded?: boolean;
  initialMapView?: "2d" | "3d";
  compact?: boolean;
} = {}) {
  const [selectedPreset, setSelectedPreset] = useState(DEFAULT_QUERY.id);
  const [selectedArtifactId, setSelectedArtifactId] = useState(DEFAULT_ANIMATION_STEP.artifactId);
  const [input, setInput] = useState<DesignVector>(DEFAULT_QUERY.input);
  const [mapView, setMapView] = useState<"2d" | "3d">(initialMapView);
  const [animationStepIndex, setAnimationStepIndex] = useState(0);
  const evaluation = useMemo(() => evaluateInterpolantChain(input), [input]);
  const certArtifact = evaluation.artifacts.find((artifact) => artifact.id === "cert-margin")!;
  const selectedArtifact = evaluation.artifacts.find((artifact) => artifact.id === selectedArtifactId) ?? certArtifact;
  const latentTuple = evaluation.latent.map((axis) => axis.value) as [number, number, number];
  const animationStep = INTERPOLANT_ANIMATION_STEPS[animationStepIndex] ?? INTERPOLANT_ANIMATION_STEPS[0]!;

  const pickPreset = (id: string) => {
    const preset = PRESET_QUERIES.find((query) => query.id === id);
    if (!preset) return;
    setSelectedPreset(id);
    setInput(preset.input);
  };

  const pickAnimationStep = (index: number) => {
    const nextStep = INTERPOLANT_ANIMATION_STEPS[index];
    if (!nextStep) return;
    const preset = PRESET_QUERIES.find((query) => query.id === nextStep.presetId);
    if (!preset) return;
    setAnimationStepIndex(index);
    setSelectedPreset(preset.id);
    setInput(preset.input);
    setSelectedArtifactId(nextStep.artifactId);
  };

  const updateDimension = (key: DimensionKey, value: number) => {
    setSelectedPreset("custom");
    setInput((current) => ({ ...current, [key]: value }));
  };

  return (
    <main className={`ipd-page ipd-v5-screen${embedded ? " ipd-embedded" : ""}`} data-testid="interpolant-provenance-demo">
      {!embedded && <header className="ipd-v5-chrome" aria-label="Thread v5 navigation">
        <div className="ipd-v5-chrome-left">
          <div className="ipd-brand">Thread</div>
          <button type="button" className="ipd-v5-project-button" aria-label="Open Thread projects">
            <span>Project</span>
            <strong>Aero certification</strong>
          </button>
          <div className="ipd-v5-run-crumb">run surrogate-audit-012</div>
        </div>
        <nav className="ipd-v5-tabs" aria-label="Thread screen tabs" data-testid="v5-interpolant-tabs">
          <button type="button">Runs</button>
          <button type="button">Evidence</button>
          <button type="button" aria-pressed="true">Interpolant audit</button>
          <button type="button">Deck</button>
        </nav>
        <div className="ipd-v5-chrome-actions">
          <div className="ipd-v5-scale-metrics" aria-label="Interpolant audit scale">
            <span><strong>{INTERPOLANT_DOMAIN.dims}</strong> dims</span>
            <span><strong>{INTERPOLANT_DOMAIN.sampledRuns}</strong> cfd</span>
            <span><strong>{evaluation.artifacts.length}</strong> aDB pts</span>
          </div>
          <span className={statusClass(evaluation.mode)} data-testid="provenance-mode">{evaluation.mode}</span>
          <button type="button">Open DAG</button>
          <button type="button">Review note</button>
        </div>
      </header>}

      <div className="ipd-shell">
        <InterpolantSlideAnimation
          activeStepIndex={animationStepIndex}
          evaluation={evaluation}
          onSelectStep={pickAnimationStep}
          selectedArtifact={selectedArtifact}
        />

        {!compact && <>
        <section className="ipd-audit-header" aria-label="Selected aDB data point">
          <div>
            <div className="ipd-kicker">Selected aDB data point</div>
            <h1>{selectedArtifact.label}</h1>
            <p>
              Audit whether this certification data point and every upstream quantity chained from the Tailor aircraft interpolant is supported
              by the recorded 12D Flow360 fit, or whether it has crossed into extrapolated evidence.
            </p>
          </div>
          <div className="ipd-audit-meta">
            <div><span>aDB point ID</span><strong>{artifactRecordId(selectedArtifact)}</strong></div>
            <div><span>Quantity</span><strong>{selectedArtifact.quantityLabel}</strong></div>
            <div><span>Operation</span><strong>{artifactOperationId(selectedArtifact)}</strong></div>
            <div><span>Certification point</span><strong>{selectedArtifact.conditionLabel}</strong></div>
            <div><span>Flight dimensions</span><strong>{INTERPOLANT_DOMAIN.dims}</strong></div>
            <div><span>CFD support cases</span><strong>{INTERPOLANT_DOMAIN.sampledRuns}</strong></div>
          </div>
        </section>

        <InterpolantDerivedPlot currentEvaluation={evaluation} selectedPreset={selectedPreset} onSelectPreset={pickPreset} />

        <section className="ipd-workspace" aria-label="Thread aDB data point audit workspace">
          <aside className="ipd-panel ipd-left-rail">
            <div className="ipd-panel-header">
              <div className="ipd-panel-title">Certification data points</div>
              <div className="ipd-panel-subtitle">Rows reveal as the animation reaches that certification point in the lineage.</div>
            </div>
            <ArtifactAuditList
              artifacts={evaluation.artifacts}
              selectedArtifactId={selectedArtifact.id}
              step={animationStep}
              onSelectArtifact={setSelectedArtifactId}
            />

            <div className="ipd-panel-header ipd-panel-header-split">
              <div>
                <div className="ipd-panel-title">Replay query</div>
                <div className="ipd-panel-subtitle">Move the recorded 12D condition and Thread recomputes the audit answer.</div>
              </div>
            </div>
            <div className="ipd-controls-body">
              <div className="ipd-preset-grid" role="group" aria-label="Preset queries">
                {PRESET_QUERIES.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    className="ipd-preset"
                    aria-pressed={selectedPreset === preset.id}
                    onClick={() => pickPreset(preset.id)}
                    title={preset.intent}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="ipd-slider-block ipd-slider-block-dense">
                {DESIGN_DIMENSIONS.map((dimension) => (
                  <label className="ipd-slider-row" key={dimension.key}>
                    <span className="ipd-slider-label">
                      <span>{dimension.shortLabel}</span>
                      <strong>{formatValue(input[dimension.key], dimension.unit)}</strong>
                    </span>
                    <input
                      aria-label={dimension.shortLabel}
                      type="range"
                      min={dimension.min - (dimension.max - dimension.min) * 0.2}
                      max={dimension.max + (dimension.max - dimension.min) * 0.2}
                      step={(dimension.max - dimension.min) / 100}
                      value={input[dimension.key]}
                      onChange={(event) => updateDimension(dimension.key, Number(event.target.value))}
                    />
                  </label>
                ))}
              </div>
            </div>
          </aside>

          <section className="ipd-panel ipd-map-panel">
            <div className="ipd-panel-header ipd-map-panel-header">
              <div className="ipd-map-heading">
                <div className="ipd-panel-title">Support evidence</div>
                <div className="ipd-panel-subtitle">3D plots all three Tailor reduced coordinates; 2D shows the lift/loading and trim/stability projection.</div>
              </div>
              <div className="ipd-map-view-toggle" role="group" aria-label="Support map dimensionality">
                <button type="button" aria-pressed={mapView === "2d"} onClick={() => setMapView("2d")}>
                  2D
                </button>
                <button type="button" aria-pressed={mapView === "3d"} onClick={() => setMapView("3d")}>
                  3D
                </button>
              </div>
            </div>
            <div className="ipd-map-body">
              {mapView === "2d" ? (
                <InterpolantSupport2D latent={latentTuple} supportCases={evaluation.supportCases} mode={evaluation.mode} />
              ) : (
                <InterpolantSupport3D
                  latent={latentTuple}
                  supportCases={evaluation.supportCases}
                  mode={evaluation.mode}
                  pointTone={animationStep.evidenceTone}
                  rotation={animationStep.rotation}
                />
              )}
              <div className="ipd-basis-strip" aria-label="Reduced aircraft basis coordinates">
                {evaluation.latent.map((axis) => (
                  <div className="ipd-basis-pill" key={axis.id}>
                    <span>{axis.label}</span>
                    <strong>{axis.value.toFixed(2)}</strong>
                  </div>
                ))}
              </div>
              <div className="ipd-support-list" aria-label="Nearest CFD support cases">
                {evaluation.supportCases.map((support) => (
                  <div className="ipd-support-item" key={support.id}>
                    <strong>{support.id}</strong>
                    <span>w {percent(support.weight)}</span>
                    <span>d {support.distance.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <aside className="ipd-panel ipd-inspector">
            <div className="ipd-panel-header">
              <div className="ipd-panel-title">Audit decision</div>
              <div className="ipd-panel-subtitle">This verdict is inherited from the source interpolant and attached to the selected data point.</div>
            </div>
            <div className="ipd-verdict-body">
              <span className={statusClass(selectedArtifact.mode)}>{selectedArtifact.mode}</span>
              <div className="ipd-metric">
                <span>Selected value</span>
                <strong data-testid="certification-margin">{selectedArtifact.value}</strong>
              </div>
              <div className="ipd-metric">
                <span>CL / CD / CM</span>
                <strong>
                  {evaluation.coefficients.cl.toFixed(3)} / {evaluation.coefficients.cd.toFixed(4)} / {evaluation.coefficients.cm.toFixed(3)}
                </strong>
              </div>
              <div className="ipd-record-block">
                <span>Parents</span>
                <strong>{selectedArtifact.parentIds.map((parentId) => ARTIFACT_RECORD_IDS[parentId] ?? parentId).join(", ") || "source interpolant"}</strong>
              </div>
              <div className="ipd-reason-list">
                {evaluation.reasons.map((reason) => (
                  <div key={reason}>{reason}</div>
                ))}
              </div>
              <div className="ipd-dimension-grid" aria-label="Dimension status">
                {evaluation.dimensionStatuses.map((dimension) => (
                  <div className={`ipd-dimension-chip ${dimension.mode}`} key={dimension.key}>
                    <span>{dimension.label}</span>
                    <strong>{formatValue(dimension.value, DESIGN_DIMENSIONS.find((item) => item.key === dimension.key)?.unit ?? "")}</strong>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>

        <section className="ipd-bottom-grid" aria-label="aDB data point lineage and ledger">
          <div className="ipd-panel">
            <div className="ipd-panel-header">
              <div className="ipd-panel-title">Data point lineage</div>
              <div className="ipd-panel-subtitle">Click any aDB point in the chain to change the audit target.</div>
            </div>
            <ArtifactChain artifacts={evaluation.artifacts} selectedArtifactId={selectedArtifact.id} onSelectArtifact={setSelectedArtifactId} />
          </div>
          <ArtifactTable artifacts={evaluation.artifacts} />
        </section>
        </>}
      </div>
    </main>
  );
}
